import { useState, useEffect, useCallback, useRef } from 'react';
import { LOADING_STATES, ERROR_MESSAGES } from '../utils/constants';
import { handleApiError } from '../services/api';

/**
 * Generic API Hook
 * Provides loading states, error handling, and data management for API calls
 * 
 * @param {Function} apiFunction - The API function to call
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Whether to fetch data automatically on mount
 * @param {Array} options.dependencies - Dependencies for useEffect
 * @param {Function} options.onSuccess - Callback function on successful API call
 * @param {Function} options.onError - Callback function on API error
 * @returns {Object} Hook state and functions
 */
export const useApi = (apiFunction, options = {}) => {
  const {
    autoFetch = true,
    dependencies = [],
    onSuccess,
    onError,
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(LOADING_STATES.IDLE);
  const [error, setError] = useState(null);

  // Use refs to store stable references to callbacks
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const apiFunctionRef = useRef(apiFunction);

  // Update refs when props change
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    apiFunctionRef.current = apiFunction;
  }, [onSuccess, onError, apiFunction]);

  /**
   * Execute the API call
   * @param {Array} params - Parameters to pass to the API function
   * @returns {Promise} API response
   */
  const execute = useCallback(async (...params) => {
    try {
      setLoading(LOADING_STATES.LOADING);
      setError(null);

      const result = await apiFunctionRef.current(...params);
      
      setData(result);
      setLoading(LOADING_STATES.SUCCESS);
      
      // Call success callback if provided
      if (onSuccessRef.current) {
        onSuccessRef.current(result);
      }
      
      return result;
    } catch (err) {
      const errorInfo = handleApiError(err);
      setError(errorInfo);
      setLoading(LOADING_STATES.ERROR);
      
      // Call error callback if provided
      if (onErrorRef.current) {
        onErrorRef.current(errorInfo, err);
      }
      
      throw err;
    }
  }, []);

  /**
   * Reset the hook state
   */
  const reset = useCallback(() => {
    setData(null);
    setLoading(LOADING_STATES.IDLE);
    setError(null);
  }, []);

  /**
   * Refresh data by calling the API again
   */
  const refresh = useCallback(async (...params) => {
    return execute(...params);
  }, [execute]);

  // Auto-fetch on mount if enabled - FIXED: No more infinite loops
  useEffect(() => {
    if (autoFetch) {
      const fetchData = async () => {
        try {
          setLoading(LOADING_STATES.LOADING);
          setError(null);

          const result = await apiFunctionRef.current();
          
          setData(result);
          setLoading(LOADING_STATES.SUCCESS);
          
          if (onSuccessRef.current) {
            onSuccessRef.current(result);
          }
        } catch (err) {
          const errorInfo = handleApiError(err);
          setError(errorInfo);
          setLoading(LOADING_STATES.ERROR);
          
          if (onErrorRef.current) {
            onErrorRef.current(errorInfo, err);
          }
        }
      };

      fetchData();
    }
  }, [autoFetch, ...dependencies]);

  return {
    // State
    data,
    loading,
    error,
    
    // Computed values
    isLoading: loading === LOADING_STATES.LOADING,
    isSuccess: loading === LOADING_STATES.SUCCESS,
    isError: loading === LOADING_STATES.ERROR,
    isIdle: loading === LOADING_STATES.IDLE,
    
    // Functions
    execute,
    reset,
    refresh,
    
    // Setters (useful for optimistic updates)
    setData,
    setLoading,
    setError,
  };
};

/**
 * Hook for API calls that don't need auto-fetching
 * Useful for form submissions, mutations, etc.
 */
export const useApiMutation = (apiFunction, options = {}) => {
  const { onSuccess, onError } = options;

  const [loading, setLoading] = useState(LOADING_STATES.IDLE);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Use refs for stable references
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const apiFunctionRef = useRef(apiFunction);

  // Update refs when props change
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    apiFunctionRef.current = apiFunction;
  }, [onSuccess, onError, apiFunction]);

  const execute = useCallback(async (...params) => {
    try {
      setLoading(LOADING_STATES.LOADING);
      setError(null);

      const result = await apiFunctionRef.current(...params);
      
      setData(result);
      setLoading(LOADING_STATES.SUCCESS);
      
      if (onSuccessRef.current) {
        onSuccessRef.current(result);
      }
      
      return result;
    } catch (err) {
      const errorInfo = handleApiError(err);
      setError(errorInfo);
      setLoading(LOADING_STATES.ERROR);
      
      if (onErrorRef.current) {
        onErrorRef.current(errorInfo, err);
      }
      
      throw err;
    }
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setLoading(LOADING_STATES.IDLE);
    setError(null);
  }, []);

  return {
    data,
    loading,
    error,
    isLoading: loading === LOADING_STATES.LOADING,
    isSuccess: loading === LOADING_STATES.SUCCESS,
    isError: loading === LOADING_STATES.ERROR,
    execute,
    reset,
  };
}; 